/* eslint-disable no-underscore-dangle */
import React from 'react';
import eventBus, {LOAD_EXTENSION_COMPLETE} from '@/eventBus';
import type {IKuToolSDKInstance} from '../types';
import {EVENT_REGISTER_TYPE, API_REQUEST_TYPE} from '../constants';
import {isDevKuTool} from '../sdk/init';
import KuAuthority from '@/auth/KuAuthority';
import {createExtensionLoader} from '@baidu/ku-tool-sdk/dist/extension-loader';
import * as store from '@/store';
import {EDITOR_MODE} from '@/Constant';
import {getGrayInfosForDocExtension, isInGrayForDocExtension} from '@/grayRelease';
import type {IGrayInfoFromServer} from '@/grayRelease/core/interface';
import {utils} from '../sdk/utils';

export const toolInstanceObj = {
    val: new Map<string, IKuToolSDKInstance>(),
    getVal: (id: string) => {
        return toolInstanceObj.val.get(id);
    },
    setVal: (id: string, val: IKuToolSDKInstance) => {
        toolInstanceObj.val.set(id, val);
    },
};

export function isAPIName(name: string) {
    const regex = /^[a-zA-Z0-9]+\.[a-zA-Z0-9]+$/;

    return typeof name === 'string' && regex.test(name);
}

const asyncApiList = [
    'doc.getDocContent',
    'doc.getDocAttachData',
    'utils.ksRequest',
    'utils.asRequest',
    'utils.getToken',
    'utils.rteRequest',
    'utils.axios',
    'user.getCreatorInfo',
];

export function isAsyncAPI(name: string) {
    return asyncApiList.includes(name);
}

export function isEventAPI(name: string, type: string) {
    const [scope] = name.split('.');

    return scope === 'event' && type === EVENT_REGISTER_TYPE;
}

export function isRequestType(type: string) {
    return [API_REQUEST_TYPE, EVENT_REGISTER_TYPE].includes(type);

}

function getLoadExtensionBusiness(curRepository: any) {
    return curRepository?.external ? 'zhishikutoc' : 'zhishiku';
}

/**
 * 加载知识库插件
 */
export async function loadExtension() {
    const editorMode: EDITOR_MODE = store.getDocumentMode();
    const {isAdmin: isRepositoryAdmin} = KuAuthority.getAuthRepository();

    const curRepository = store.getData(store.KEYS.CUR_REPOSITORY) || {};
    const repositoryId = curRepository.repositoryGuid;

    const curDocument = store.getData(store.KEYS.CUR_DOCUMENT) || {};
    const {docGuid} = curDocument;

    // 加载开放插件
    const extensionLoader = createExtensionLoader();
    await extensionLoader.loadExtension({
        business: getLoadExtensionBusiness(curRepository),
        kuEnv: process.env.REACT_APP_ENVIRONMENT,
        isDevKuTool,
        repositoryId,
        isRepositoryAdmin,
        docId: docGuid,
        editorMode,
        getSmallFlowConfig: getGrayInfosForDocExtension,
        isMatchSmallFlow: (
            smallFlowConfig: IGrayInfoFromServer,
            key: string
        ) => {
            return Boolean(isInGrayForDocExtension(smallFlowConfig, key, repositoryId));
        },
        report: (options: {
            id: string;
            params?: any;
            type?: any;
        }) => {
            const {id, params, type} = options ?? {};
            utils.kuLogger({
                repId: id,
                params,
                type,
            });
        },
    });

    // 触发加载完成事件
    eventBus.emit(LOAD_EXTENSION_COMPLETE);
}

/**
 * 上报场景下对接口参数的序列化处理
 * @param apiParams api 参数
 */
export function apiParamsHandleForReport(apiParams: any): string {
    let result = '';
    try {
        result = JSON.stringify(apiParams, (key, value) => {
            if (typeof value === 'function') {
                return `Function: ${value.name}`;
            }
            if (React.isValidElement(value)) {
                return `React Component: ${value.type}`;
            }
            // 埋点上报字符串属性值，最多 200 个字符
            if (typeof value === 'string' && value.length > 200) {
                return value.slice(0, 200) + '...';
            }
            return value;
        });
    } catch {
        result = '参数解析失败';
    }
    return result;
}

interface IFlexBuilderItem {
    builderId: string;
    title: string;
    blockId: string;
    createTime: string;
}

// json 节点场景：随心搭组件信息数据
type IFlexBuilderJsonItem = Omit<IFlexBuilderItem, 'createTime'>;

export const flexBuilderInstanceObj = {
    val: new Map<string, IFlexBuilderItem>(),
    getList: () => {
        return Array.from(flexBuilderInstanceObj.val.values());
    },
    get: (id: string) => {
        return flexBuilderInstanceObj.val.get(id);
    },
    set: (id: string, data: IFlexBuilderItem) => {
        if (id && data?.blockId) {
            flexBuilderInstanceObj.val.set(id, data);
        }
    },
    delete: (id: string) => {
        flexBuilderInstanceObj.val.delete(id);
    },
    clear: () => {
        flexBuilderInstanceObj.val.clear();
    },
    getListByJSON: () => {
        const editorRef = store.getEditorRef();

        if (!editorRef.current || typeof editorRef.current.getEditorInstance !== 'function') {
            return [];
        }

        try {
            const instance = editorRef.current.getEditorInstance();
            const json = instance.children;

            if (!Array.isArray(json)) {
                return [];
            }

            const result: IFlexBuilderJsonItem[] = [];
            const stack = [...json].reverse();
            while (stack.length > 0) {
                const node = stack.pop();
                if (!node) {
                    continue;
                }

                if (node.type === 'flex-builder') {
                    const {blockId, title, builderId} = node;
                    result.push({
                        builderId,
                        title: title || flexBuilderInstanceObj.get(builderId)?.title || '',
                        blockId,
                    });
                }

                if (Array.isArray(node.children)) {
                    for (let i = node.children.length - 1; i >= 0; i--) {
                        stack.push(node.children[i]);
                    }
                }
            }
            return result.filter(item => item.title);
        } catch (e) {
            return [];
        }
    },
};
